import { Box, Typography, useTheme } from "@mui/material";
import {
  DefaultLink,
  DefaultNode,
  ResponsiveSankey,
  SankeyDataProps,
} from "@nivo/sankey";
import LegendsCharts from "../../legends";
import React, { useEffect, useState } from "react";
import handleColors from "../utils/handleColors";
import useStyles from "../styles";
import TooltipCharts from "../../toolTipCharts";
import { MySankeyDashConfig } from "./types";
import { CssMixBlendMode } from "@nivo/core";
import maskUnits from "../../../utils/maskUnits";
import { useMediaQuery } from "@mui/system";
import { getColorsArray } from "../../../utils/getColorsPallet";
import calculateMetrics from "../utils/calculateMetrics";
// const formatData=
interface FormatSeriesProps {
  nodes: DefaultNode[];
  links: DefaultLink[];
}
// const formatSeries = (
//   data: DefaultLink[],
//   indexBy: string,
//   keys: string[]
// ): FormatSeriesProps => ({
//   nodes: keys.map((key) => ({ id: key })) as DefaultNode[],
//   links: data as DefaultLink[],
// });
const formatData = (
  data: MyDatum[],
  indexBy: string,
  codeIndexBy: string,
  keys: string[],
): any => {
  const agregateDataByCode = data.reduce((acc: any[], d: any) => {
    // const validDate = new Date(String(d["vencimentoConta"])) > new Date();
    const code = String(d[codeIndexBy]);
    const splitCode = code.split(".");
    if (splitCode.length <= 3) {
      const findCode = Array.isArray(acc)
        ? acc.findIndex(
            (item: any) =>
              item[codeIndexBy] === code && d[indexBy] === item[indexBy],
          )
        : -1;

      if (findCode >= 0) {
        keys.forEach((key) => {
          acc[findCode][key] = (acc[findCode][key] || 0) + (d[key] || 0);
        });
      } else {
        acc = [...acc, { ...d }];
      }
    }
    return acc;
  }, []);
  const links = agregateDataByCode.reduce((acc: any[], d: any) => {
    const code = String(d[codeIndexBy]);

    const splitCode = code.split(".").reduce((acc: any[], sc: any, index) => {
      if (index > 0) {
        const unionCode = acc[index - 1] + "." + sc;
        acc = [...acc, unionCode];
      } else {
        acc = [...acc, sc];
      }
      return acc;
    }, []);

    if (splitCode.length > 1) {
      // let nodeFind = agregateDataByCode.find((a) =>
      //   splitCode.find((sc) => sc === a[codeIndexBy] && code !== a[codeIndexBy])
      // );
      const nodeFind = agregateDataByCode.find(
        (a) => splitCode.includes(a[codeIndexBy]) && code !== a[codeIndexBy],
      );
      if (nodeFind) {
        const nodeSource = nodeFind[indexBy];
        const nodeTarget = d[indexBy];

        acc = [
          ...acc,
          {
            source: nodeSource,
            target: nodeTarget,
            value: d[keys[0]] || 0,
          },
        ];
      }
    }
    return acc;
  }, []);
  const nodes = agregateDataByCode.map((d) => ({
    id: String(d[indexBy]),
  }));
  return { nodes, links };
};
const SankeyChart: React.FC<MySankeyDashConfig> = (props) => {
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  let { data, metricsType, ...otherProps } = props;
  const { keys, indexBy, codeIndexBy, units, fractionDigits } = props;
  const sankeyRef = React.useRef(null);
  const theme = useTheme();
  const styles = useStyles(theme);

  const handleResize = () => {
    if (sankeyRef.current) {
      const { offsetHeight, offsetWidth } = sankeyRef.current;
      setBoxHeight(offsetHeight);
      setBoxWidth(offsetWidth);
    }
  };
  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const formattedData =
    data?.length > 0 ? formatData(data, indexBy, codeIndexBy, keys) : undefined;

  const isValidSankeyData =
    formattedData &&
    Array.isArray(formattedData.nodes) &&
    Array.isArray(formattedData.links) &&
    formattedData.nodes.length > 0 &&
    formattedData.links.length > 0 &&
    formattedData.nodes.every((n: any) => typeof n.id === "string") &&
    formattedData.links.every(
      (l: any) =>
        typeof l.source === "string" &&
        typeof l.target === "string" &&
        typeof l.value === "number" &&
        !isNaN(l.value),
    );

  const defaultBarChartConfig = {
    margin: { top: 10, right: 24, bottom: 24, left: 24 },
    linkBlendMode:
      theme.palette.mode === "dark"
        ? ("lighten" as CssMixBlendMode)
        : ("darken" as CssMixBlendMode),
    sort: "auto" as "input" | "ascending" | "descending",
    linkHoverOthersOpacity: 0.7,
    linkHoverOpacity: 1,
    nodeHoverOthersOpacity: 0.7,
    nodeHoverOpacity: 1,
    enableLinkGradient: true,
    nodeThickness: 12,
    nodeInnerPadding: 2,
    nodeSpacing: 10,
    nodeBorderRadius: 4,
    nodeBorderWidth: 0,
    linkContract: 0.5,
    linkOpacity: 0.9,
    nodeOpacity: 1,
    labelTextColor: theme.palette.text.primary,
    labelPosition: "inside" as "inside" | "outside",
    labelOrientation: "horizontal" as "horizontal" | "vertical" | undefined,
    label: (node: { id: string; value: number }) => node.id + " " + node.value,
    theme: {
      axis: {
        legend: {
          text: styles.fontsizeTicks,
        },
      },
    },
  };

  const mergedProps = { ...defaultBarChartConfig, ...otherProps };
  return isValidSankeyData ? (
    <Box sx={[styles.boxContainer, { flexDirection: "column" }]}>
      <Box sx={styles.boxContainerSizeChart} ref={sankeyRef}>
        <Box sx={styles.boxChart(boxHeight, boxWidth)}>
          <ResponsiveSankey
            key={"sankey"}
            animate={false}
            data={formattedData}
            {...mergedProps}
            {...(props.colors
              ? {
                  colors: getColorsArray(
                    formattedData?.nodes?.length,
                    props.colors,
                    theme.palette.mode === "dark",
                  ),
                }
              : {})}
            {...(props.colorsByKey
              ? {
                  colors: (params) =>
                    handleColors(params.id, theme, props.colorsByKey),
                }
              : {})}
            label={({ value }) =>
              maskUnits(value, units, fractionDigits ?? 3) + ""
            }
            linkTooltip={({ link }) => (
              <TooltipCharts
                valueX={link.source.id}
                valueY={link.target.id}
                valueZ={maskUnits(link.value, units, fractionDigits ?? 3)}
                color={link.color}
              />
            )}
            nodeTooltip={({ node }) => (
              <TooltipCharts
                valueX={node.id}
                valueZ={maskUnits(node.value, units, fractionDigits ?? 3)}
                color={node.color}
              />
            )}
          />
        </Box>
      </Box>
      <Box
        sx={[
          styles.boxLegend,
          {
            width: "fit-content",
            height: "fit-content",
            minHeight: metricsType ? "45px" : "25px",
          },
        ]}
      >
        <LegendsCharts
          {...props}
          direction="row"
          keys={[...(formattedData?.nodes.map((n: any) => n.id) || [])]}
          metricsType={
            metricsType
              ? calculateMetrics(metricsType, data as any[])
              : undefined
          }
          //colorsByKey={props.colorsByKey}
        />
      </Box>
    </Box>
  ) : (
    <></>
  );
};

export default SankeyChart;
